import * as React from 'react';
import { Drawer, Box, Typography, IconButton, List, Button, Chip, Divider, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { NotificationItem } from './NotificationItem';
import { AppNotification, NotificationApiResponse } from './types';
import { mapToAppNotification } from './notificationMapper';
import { useDataProvider, useNotify } from 'react-admin';
import { useEffect, useCallback } from 'react';

const PER_PAGE = 20; // 1ページあたりの件数

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ open, onClose }) => {
  // 1. ページネーションを管理するためのStateを追加・変更
  const [notifications, setNotifications] = React.useState<AppNotification[]>([]);
  const [page, setPage] = React.useState(1);
  const [initialLoading, setInitialLoading] = React.useState(true); // 初回ロード用のState
  const [loadingMore, setLoadingMore] = React.useState(false); // 「もっと見る」用のState
  const [hasMore, setHasMore] = React.useState(true); // 追加データがあるか

  const dataProvider = useDataProvider();
  const notify = useNotify();
  // useGetList とそれを使っていた useEffect はここから削除します

  // 2. データ取得ロジックを独立した関数として定義
  const fetchNotifications = useCallback(
    async (requestedPage: number) => {
      // ローディング状態を設定
      if (requestedPage === 1) {
        setInitialLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const { data, total } = await dataProvider.getList<NotificationApiResponse>('notifications', {
          pagination: { page: requestedPage, perPage: PER_PAGE },
          sort: { field: 'publishedAt', order: 'DESC' },
          filter: {},
        });

        const newNotifications = data.map(mapToAppNotification);

        setNotifications((prev) => (requestedPage === 1 ? newNotifications : [...prev, ...newNotifications]));

        // 全件取得し終わったか判定
        const currentCount = (requestedPage - 1) * PER_PAGE + newNotifications.length;
        if (total === undefined || currentCount >= total) {
          setHasMore(false);
        }
      } catch (err) {
        console.error(err);
        notify('通知の読み込みに失敗しました', { type: 'error' });
      } finally {
        if (requestedPage === 1) {
          setInitialLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [dataProvider, notify]
  );

  // 3. パネルが開いたときに初回データを取得するEffect
  useEffect(() => {
    if (open) {
      // 各Stateを初期化
      setNotifications([]);
      setHasMore(true);
      setPage(1); // ページを1にリセット
      // 初回データを取得し、完了したら次に読み込むページを2に設定
      fetchNotifications(1).then(() => {
        setPage(2);
      });
    }
  }, [open, fetchNotifications]);

  // 「もっと見る」ボタンのクリックハンドラ
  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    fetchNotifications(page).then(() => {
      setPage((prev) => prev + 1);
    });
  };

  // const unreadCount = notifications.filter((n) => !n.isRead).length;

  // handleMarkAsRead は変更の必要がないため、そのままです
  const handleMarkAsRead = async (id: AppNotification['id']) => {
    const originalNotifications = [...notifications];
    const notificationToUpdate = originalNotifications.find((n) => n.id === id);

    if (!notificationToUpdate || notificationToUpdate.isRead) {
      return;
    }

    try {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      await dataProvider.readNotify(id);
    } catch (e) {
      setNotifications(originalNotifications);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 480, maxWidth: '100vw', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header (変更なし) */}
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, flexShrink: 0 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            お知らせ
          </Typography>
          {/* {unreadCount > 0 && <Chip label={unreadCount} color="error" size="small" sx={{ mr: 2 }} />} */}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        {/* Notification List を修正 */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <List disablePadding>
                {notifications.map((notification) => (
                  <NotificationItem key={notification.id} notification={notification} onMarkAsRead={handleMarkAsRead} />
                ))}
              </List>
              {/* 4. ページネーションUIを追加 */}
              <Box sx={{ textAlign: 'center', p: 2 }}>
                {loadingMore && <CircularProgress size={24} />}
                {!loadingMore && hasMore && (
                  <Button onClick={handleLoadMore} fullWidth>
                    もっと見る
                  </Button>
                )}
                {!loadingMore && !hasMore && notifications.length > 0 && notifications.length >= PER_PAGE && (
                  <Typography variant="caption" color="text.secondary">
                    これ以上お知らせはありません
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};